// Mantine theme. The look follows the SmarDac Hub app (Inter, compact type, square surfaces,
// uppercase micro-labels, primary #3454d1) instead of the doc 09 tokens — see docs/DECISIONS.md.
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  createTheme,
  Drawer,
  Input,
  InputWrapper,
  Menu,
  Modal,
  Paper,
  Table,
  Tabs,
  Tooltip,
  type MantineColorsTuple,
} from '@mantine/core';

/** Ten shades around the Hub primary; index 6 is the brand colour itself. */
const brand: MantineColorsTuple = [
  '#eef2fd',
  '#dbe2f8',
  '#b5c2f0',
  '#8da0e8',
  '#6b83e1',
  '#4f6bd9',
  '#3454d1',
  '#2a46b5',
  '#233c9c',
  '#1b3083',
];

export const tokens = {
  bg: '#f3f4f6',
  surface: '#ffffff',
  border: '#e5e7eb',
  borderStrong: '#dcdee4',
  text: '#283c50',
  textStrong: '#0f172a',
  textBody: '#475569',
  textMuted: '#64748b',
  hover: '#eaebef',
  primary: '#3454d1',
  success: '#17a35a',
  warning: '#c77700',
  danger: '#d63939',
  info: '#1c8f88',
  headerHeight: 64,
  navWidth: 241,
  navCollapsedWidth: 72,
} as const;

/** Colour-blind-safe chart series order (doc 09 §1), led by the brand blue. */
export const chartPalette = [
  '#3454d1',
  '#D9620F',
  '#0E8F7E',
  '#6B4FA0',
  '#B8860B',
  '#4C5BD4',
  '#A23B72',
  '#55616D',
] as const;

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

export const theme = createTheme({
  primaryColor: 'brand',
  primaryShade: 6,
  colors: { brand },
  fontFamily: FONT,
  fontFamilyMonospace: "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace",
  headings: {
    fontFamily: FONT,
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '1.5rem', lineHeight: '1.25' },
      h2: { fontSize: '1.25rem', lineHeight: '1.3' },
      h3: { fontSize: '1rem', lineHeight: '1.35' },
      h4: { fontSize: '0.9375rem', lineHeight: '1.4' },
    },
  },
  fontSizes: { xs: '0.6875rem', sm: '0.75rem', md: '0.84rem', lg: '1rem', xl: '1.125rem' },
  lineHeights: { xs: '1.4', sm: '1.45', md: '1.6', lg: '1.6', xl: '1.6' },
  defaultRadius: 0,
  radius: { xs: '3px', sm: '5px', md: '10px', lg: '15px', xl: '20px' },
  spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
  black: tokens.text,
  shadows: {
    xs: '0 1px 3px 0 rgb(0 0 0 / .1), 0 1px 2px -1px rgb(0 0 0 / .1)',
    sm: '0 1px 5px rgba(40, 60, 80, 0.15)',
    md: '0 10px 24px 0 rgba(62, 57, 107, 0.18)',
  },
  focusRing: 'auto',
  cursorType: 'pointer',
  components: {
    Button: Button.extend({
      defaultProps: { size: 'sm' },
      styles: {
        root: { fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.6875rem' },
      },
    }),
    ActionIcon: ActionIcon.extend({ defaultProps: { variant: 'subtle', color: 'gray', radius: 'xl' } }),
    Input: Input.extend({ defaultProps: { radius: 'sm' } }),
    InputWrapper: InputWrapper.extend({
      styles: { label: { fontSize: '0.75rem', fontWeight: 600, color: tokens.text, marginBottom: 4 } },
    }),
    Card: Card.extend({ defaultProps: { shadow: 'xs', padding: 'lg', radius: 0 } }),
    Paper: Paper.extend({ defaultProps: { radius: 0 } }),
    Modal: Modal.extend({
      // A click outside never closes a dialog (owner, 2026-10-23): only Close, Cancel or Escape do.
      defaultProps: { radius: 0, centered: true, closeOnClickOutside: false, overlayProps: { backgroundOpacity: 0.45, color: '#022142' } },
      styles: { title: { fontWeight: 700, fontSize: '1rem' } },
    }),
    // Side panels too: a click outside does not close them (owner, 2026-10-23).
    Drawer: Drawer.extend({ defaultProps: { closeOnClickOutside: false }, styles: { title: { fontWeight: 700, fontSize: '1rem' } } }),
    Badge: Badge.extend({
      defaultProps: { radius: 'xs', variant: 'light' },
      styles: { root: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 } },
    }),
    Menu: Menu.extend({
      defaultProps: { shadow: 'md', radius: 0 },
      styles: { item: { fontSize: '0.8125rem', fontWeight: 600, borderRadius: 5 } },
    }),
    Tooltip: Tooltip.extend({ defaultProps: { withArrow: true, radius: 'xs' } }),
    Table: Table.extend({
      defaultProps: { verticalSpacing: 8, horizontalSpacing: 'md', highlightOnHover: true },
      styles: {
        th: {
          fontSize: '0.625rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: tokens.text,
          whiteSpace: 'nowrap',
        },
        td: { fontSize: '0.8125rem' },
      },
    }),
    Tabs: Tabs.extend({ styles: { tab: { fontSize: '0.75rem', fontWeight: 600 } } }),
  },
});
